import {
  DEFAULT_PRESET,
  type DatePresetValue,
} from "#app/lib/utils/date-presets";
import { useDashboardScope, type DashboardScope } from "./dashboard-scope-context";
import type { Omittable } from "@salestrak/ui";

/**
 * Infrastructure shared by every scope→`where` fold (`useScopedWhere` for issues,
 * `useScopedActivityWhere` for activities). A fold reads the ambient dashboard
 * window, folds in a per-widget override, and maps the EFFECTIVE facets onto its own
 * query shape — the only thing that differs between folds is the target `where`
 * shape, never how the window/override/default is resolved. Keeping that here means
 * there is one canonical answer for "what window is this widget actually on."
 */

/**
 * The window a widget falls back to when it renders OUTSIDE a dashboard — the
 * Add-Card gallery preview and the maximized-widget overlay both mount a widget with
 * no surrounding `DashboardScopeProvider`. No entity, no member narrowing, the
 * default date preset: representative data, never a throw.
 */
export const DEFAULT_SCOPE: DashboardScope = {
  entity: null,
  entityWhere: null,
  dateRange: { key: DEFAULT_PRESET },
  memberIds: [],
  onlyMe: false,
};

/**
 * A per-widget override of individual scope facets — so a widget can own its own date
 * range / member set / Only-Me for its OWN data, without abandoning the centralized
 * fold. Only `undefined` inherits the ambient value; an explicit `[]` / `false` is
 * honored (force-off). `entity`/`entityWhere` are intentionally NOT overridable — the
 * dashboard pins the entity a scoped view reports on.
 */
export type ScopedWhereOverride = Omittable<{
  dateRange?: DatePresetValue;
  memberIds?: string[];
  onlyMe?: boolean;
}>;

export type ScopedWhereOptions = Omittable<{
  /**
   * Which date column the window filters on. Pass `null` to ignore the window
   * entirely (e.g. an all-time "open projects" count). Defaults to `creationDate`.
   */
  dateField?: "creationDate" | "lastUpdateDate" | null;
  /** Per-widget overrides of the ambient window (see {@link ScopedWhereOverride}). */
  override?: ScopedWhereOverride;
}>;

/** The effective facets a fold builds its `where` from — ambient scope with the
 *  default filled in off-dashboard and any per-widget override already merged. */
export type EffectiveScope = {
  /** The ambient scope, never null (default off-dashboard). Its `entity`/`entityWhere`
   *  are the dashboard-pinned facets a fold reads directly (never overridable). */
  scope: DashboardScope;
  effDateRange: DatePresetValue;
  effMemberIds: string[];
  effOnlyMe: boolean;
};

/**
 * Reads the ambient {@link DashboardScope} (falling back to {@link DEFAULT_SCOPE}
 * off-dashboard) and resolves each overridable facet with `??` — only `undefined`
 * inherits the ambient value, so an explicit `[]` / `false` force-off is honored.
 * Every fold starts here, then maps these effective values onto its query shape.
 */
export function useEffectiveScope(override?: ScopedWhereOverride): EffectiveScope {
  const ambient = useDashboardScope();
  const scope = ambient ?? DEFAULT_SCOPE;
  return {
    scope,
    effDateRange: override?.dateRange ?? scope.dateRange,
    effMemberIds: override?.memberIds ?? scope.memberIds,
    effOnlyMe: override?.onlyMe ?? scope.onlyMe,
  };
}
